import { ProductSlugs } from '@deque/billing-utils';
import { Notice } from '@deque/cauldron-react';
import React, { useMemo } from 'react';
import { useWatch } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { type FieldMappingField } from '~/app/types';
import { useEnterprises } from '~/common/contexts/enterprises';
import { getAxeFieldsOptions } from './FieldMapping';
import styles from './FieldMapping.css';

export const unavailableInAxeMonitorFields = [
  'screenshotURL',
  'tags',
  'testName',
  'shareURL',
  'testUrl',
  'foundBy',
  'ruleId'
];

export const CompatibilityWarning = () => {
  const { t } = useTranslation();

  const fieldMapping = useWatch({ name: 'fieldMapping' });
  const { activeEnterprise } = useEnterprises();

  const fieldTitlesByValue = useMemo(() => {
    const axeOptions = getAxeFieldsOptions(t);

    const fieldTitleMap = axeOptions.reduce<Record<string, string>>(
      (map, option) => {
        map[option.value] = option.label;
        return map;
      },
      {}
    );

    return fieldTitleMap;
  }, [t]);

  const unavailableFields = useMemo(() => {
    const hasAxeMonitorSubscription = activeEnterprise?.subscriptions.find(
      ({ product_slug, purchase_state }) =>
        product_slug === ProductSlugs.axeMonitor && purchase_state !== 'none'
    );

    if (!fieldMapping || !hasAxeMonitorSubscription) {
      return [];
    }

    const mappingFields = fieldMapping.map(
      ({ axeField }: FieldMappingField) => axeField
    );

    const unavailable = unavailableInAxeMonitorFields.filter(field =>
      mappingFields.includes(field)
    );

    return unavailable;
  }, [fieldMapping, activeEnterprise?.subscriptions]);

  if (!unavailableFields.length) {
    return null;
  }

  return (
    <Notice
      title={t('Template Compatibility Issue')}
      type="caution"
      className={styles.compatibilityWarning}
    >
      {t('This template includes fields not available in every Deque product:')}
      <ul>
        {unavailableFields.map(field => (
          <li key={`warning-${field}`}>{fieldTitlesByValue[field]}</li>
        ))}
      </ul>
      {t('Users can set these manually when sending issues to JIRA.')}
    </Notice>
  );
};
